import { Link, useLoaderData, useNavigate } from "react-router-dom";
import { Instant } from "@js-joda/core";
import { Modal } from "react-bootstrap";
import { showInstantLocalDateTime } from "./Data/Date";
import { authAtom } from "./Data/Atoms";
import { useAtom } from "jotai";
import { useEffect, useState } from "react";
import { Input } from "./Controls/Input";
import { Button } from "./Controls/Button";
import { Alphabet, Asterisk, At } from "react-bootstrap-icons";
import { PageTitle } from "./Controls/PageTitle";
import { LargePage } from "./Controls/LargePage";
import { HeaderText } from "./Controls/HeaderText";
import { UserPageDeleteAccountModal } from "./UserPageDeleteAccountModal";
import { Api, ChgEmailAddressReq, ChgPassReq, showRole } from "./Data/Api";
import { StaffNotification, StaffNotificationUpdateReq } from "./Data/ApiTypes";
import { NotificationType } from "./Data/ApiTransport";
import { toast } from "react-toastify";
import { processResult } from "./Data/Result";
import { userCanSeeNotificationOptIn } from "./helpers/RoleManager";

interface ChangeNameProps {
  show: boolean;
  setShow: (show: boolean) => void;
}

export function ChangeNameModal({ show, setShow }: ChangeNameProps) {
  const [name, setName] = useState("");

  const handleSubmit = () => {
    setShow(false);
  };

  return (
    <Modal centered show={show} onEscapeKeyDown={() => setShow(false)}>
      <Modal.Header>
        <HeaderText>Change Name</HeaderText>
      </Modal.Header>
      <Modal.Body>
        <div>
          <Input
            type="text"
            icon={<Alphabet />}
            label="Enter New Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="text-end d-flex flex-row justify-content-end">
          <Button className="w-50 m-2" onClick={() => setShow(false)}>
            Cancel
          </Button>
          <Button className="w-50 m-2" onClick={handleSubmit}>
            Change
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}

interface ChangeEmailModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
}

export function ChangeEmailModal({ show, setShow }: ChangeEmailModalProps) {
  const [form, setForm] = useState<ChgEmailAddressReq>({
    currentPassword: "",
    newEmailAddress: "",
  });

  const [error, setError] = useState<string | null>(null);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = () => {
    setError(null);
    Api.changeEmailAddress(form)
      .then((res) => {
        if (res.isOk) {
          console.log("Email changed successfully:", res.value);
          setShow(false);
        } else {
          setError(res.error);
        }
      })
      .catch((err) => {
        console.error("API call failed:", err);
        setError("An unexpected error occurred. Please try again.");
      });
  };

  return (
    <Modal centered show={show} onEscapeKeyDown={() => setShow(false)}>
      <Modal.Header>
        <HeaderText>Change Email Address</HeaderText>
      </Modal.Header>
      <Modal.Body>
        <div>
          <Input
            type="password"
            icon={<Asterisk />}
            label="Enter Current Password"
            name="currentPassword"
            value={form.currentPassword}
            onChange={handleChange}
          />
        </div>
        <div>
          <Input
            type="email"
            icon={<At />}
            label="Enter New Email Address"
            name="newEmailAddress"
            value={form.newEmailAddress}
            onChange={handleChange}
          />
        </div>
        {error && <div className="text-danger">{error}</div>}
        <div className="text-end d-flex flex-row justify-content-end">
          <Button className="w-50 m-2" onClick={() => setShow(false)}>
            Cancel
          </Button>
          <Button className="w-50 m-2" onClick={handleSubmit}>
            Change
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}

interface ChangePasswordModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
}

export function ChangePasswordModal({
  show,
  setShow,
}: ChangePasswordModalProps) {
  const [form, setForm] = useState<ChgPassReq>({
    currentPassword: "",
    newPassword: "",
  });
  const [error, setError] = useState<string | null>(null);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = () => {
    setError(null);
    Api.changePassword(form)
      .then((res) => {
        if (res.isOk) {
          console.log("Password changed successfully:", res.value);
          setShow(false);
        } else {
          setError(res.error);
        }
      })
      .catch((err) => {
        console.error("API call failed:", err);
        setError("An unexpected error occurred. Please try again.");
      });
  };

  return (
    <Modal centered show={show} onEscapeKeyDown={() => setShow(false)}>
      <Modal.Header>
        <HeaderText>Change Password</HeaderText>
      </Modal.Header>
      <Modal.Body>
        <div>
          <Input
            type="password"
            icon={<Asterisk />}
            label="Enter Current Password"
            name="currentPassword"
            value={form.currentPassword}
            onChange={handleChange}
          />
        </div>
        <div>
          <Input
            type="password"
            icon={<Asterisk />}
            label="Enter New Password"
            name="newPassword"
            value={form.newPassword}
            onChange={handleChange}
          />
        </div>
        {error && <div className="text-danger">{error}</div>}
        <div className="text-end d-flex flex-row justify-content-end">
          <Button className="w-50 m-2" onClick={() => setShow(false)}>
            Cancel
          </Button>
          <Button className="w-50 m-2" onClick={handleSubmit}>
            Change
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export function UserProfilePage() {
  const nav = useNavigate();
  const [auth, setAuth] = useAtom(authAtom);
  const loadedStaffNotifications = useLoaderData() as StaffNotification[];
  const [staffNotifications, setStaffNotifications] = useState(
    loadedStaffNotifications
  );
  const [showChgEmail, setShowChgEmail] = useState(false);
  const [showChgPass, setShowChgPass] = useState(false);
  const [showChgName, setShowChgName] = useState(false);
  const [showDel, setShowDel] = useState(false);
  const canSee = (t: NotificationType) => userCanSeeNotificationOptIn(auth.account?.roles, t);
  const [
    enableNotificationForMonthlyApplication,
    setEnableNotificationForMonthlyApplication,
  ] = useState(false);
 
  const [
    enableNotificationForMonthlyApplicationDaily,
    setEnableNotificationForMonthlyApplicationDaily,
  ] = useState(false);

  const [
    enableNotificationForRenewalRetries,
    setEnableNotificationForRenewalRetries,
  ] = useState(false);

  const [
    enableNotificationForDispute,
    setEnableNotificationForDispute,
  ] = useState(false);

  const [
    enableNotificationForParkerSubscriptionCancellation,
    setEnableNotificationForParkerSubscriptionCancellation,
  ] = useState(false);

  const [
    enableNotificationForParkerSubscriptionModified,
    setEnableNotificationForParkerSubscriptionModified,
  ] = useState(false);

  const [
    enableNotificationForValidationRequestCreated,
    setEnableNotificationForValidationRequestCreated,
  ] = useState(false);

  const [
    enableNotificationForValidationRequestPurchased,
    setEnableNotificationForValidationRequestPurchased,
  ] = useState(false);

  const [
    enableNotificationForTransientParkerPaid,
    setEnableNotificationForTransientParkerPaid,
  ] = useState(false);

  const [
    enableNotificationForRenewalsSummary,
    setEnableNotificationForRenewalsSummary,
  ] = useState(false);

    const [
    enableNotificationForACHPaymentFailure,
    setEnableNotificationForACHPaymentFailure,
  ] = useState(false);

  const [
    enableNotificationForRenewalDeactivationSummary,
    setEnableNotificationForRenewalDeactivationSummary,
  ] = useState(false);

  useEffect(() => {
    const enableNotificationForMonthlyApplication = staffNotifications.some(
      (x) => x.notificationType == NotificationType.MonthlyApplication
    );
    setEnableNotificationForMonthlyApplication(
      enableNotificationForMonthlyApplication
    );
  }, [staffNotifications]);

  useEffect(() => {
    const enableNotificationForMonthlyApplicationDaily = staffNotifications.some(
      (x) => x.notificationType == NotificationType.MonthlyApplicationDaily
    );
    setEnableNotificationForMonthlyApplicationDaily(
      enableNotificationForMonthlyApplicationDaily
    );
  }, [staffNotifications]);

    useEffect(() => {
    const enableNotificationForRenewalRetries = staffNotifications.some(
      (x) => x.notificationType == NotificationType.RenewalRetriesExhausted
    );
    setEnableNotificationForRenewalRetries(
      enableNotificationForRenewalRetries
    );
  }, [staffNotifications]);

    useEffect(() => {
    const enableNotificationForDispute = staffNotifications.some(
      (x) => x.notificationType == NotificationType.DisputeAlert
    );
    setEnableNotificationForDispute(
      enableNotificationForDispute
    );
  }, [staffNotifications]);

    useEffect(() => {
    const enableNotificationForParkerSubscriptionCancellation = staffNotifications.some(
      (x) => x.notificationType == NotificationType.ParkerSubscriptionCancellation
    );
    setEnableNotificationForParkerSubscriptionCancellation(
      enableNotificationForParkerSubscriptionCancellation
    );
  }, [staffNotifications]);

    useEffect(() => {
    const enableNotificationForParkerSubscriptionModified = staffNotifications.some(
      (x) => x.notificationType == NotificationType.ParkerSubscriptionModified
    );
    setEnableNotificationForParkerSubscriptionModified(
      enableNotificationForParkerSubscriptionModified
    );
  }, [staffNotifications]);

    useEffect(() => {
    const enableNotificationForValidationRequestCreated = staffNotifications.some(
      (x) => x.notificationType == NotificationType.ValidationRequestCreated
    );
    setEnableNotificationForValidationRequestCreated(
      enableNotificationForValidationRequestCreated
    );
  }, [staffNotifications]);

    useEffect(() => {
    const enableNotificationForValidationRequestPurchased = staffNotifications.some(
      (x) => x.notificationType == NotificationType.ValidationRequestPurchased
    );
    setEnableNotificationForValidationRequestPurchased(
      enableNotificationForValidationRequestPurchased
    );
  }, [staffNotifications]);

  useEffect(() => {
    const enableNotificationForTransientParkerPaid = staffNotifications.some(
      (x) => x.notificationType == NotificationType.TransientParkerPaid
    );
    setEnableNotificationForTransientParkerPaid(
      enableNotificationForTransientParkerPaid
    );
  }, [staffNotifications]);

  useEffect(() => {
    const enableNotificationForRenewalsSummary = staffNotifications.some(
      (x) => x.notificationType == NotificationType.SubscriptionRenewalSummary
    );
    setEnableNotificationForRenewalsSummary(
      enableNotificationForRenewalsSummary
    );
  }, [staffNotifications]);

  useEffect(() => {
    const enableNotificationForACHPaymentFailure = staffNotifications.some(
      (x) => x.notificationType == NotificationType.ACHPaymentFailure
    );
    setEnableNotificationForACHPaymentFailure(
      enableNotificationForACHPaymentFailure
    );
  }, [staffNotifications]);

  useEffect(() => {
    const enableNotificationForRenewalDeactivationSummary = staffNotifications.some(
      (x) => x.notificationType == NotificationType.SubscriptionRenewalDeactivationSummary
    );
    setEnableNotificationForRenewalDeactivationSummary(
      enableNotificationForRenewalDeactivationSummary
    );
  }, [staffNotifications]);

  const changeNotificationStatus = async (
    enable: boolean,
    notificationType: NotificationType
  ) => {
    const req: StaffNotificationUpdateReq = {
      enable: enable,
      notificationType: notificationType,
    };

    const res = await Api.changeStaffNotification(req);

    processResult(
      res,
      (updated: StaffNotification) => {
        setStaffNotifications((prev) => {
          if (enable) {
            if (!prev.some((n) => n.notificationType === notificationType)) {
              return [...prev, updated];
            }
            return prev;
          } else {
            return prev.filter(
              (n) => n.staffNotificationId !== updated.staffNotificationId
            );
          }
        });
      },
      (err: any) => {
        toast.error("Failed to change subscription status:", err);
      }
    );
  };

  if (!auth.account || !auth.isLoggedIn) {
    return;
  }

  return (
    <LargePage>
      <ChangeNameModal show={showChgName} setShow={setShowChgName} />

      <ChangeEmailModal show={showChgEmail} setShow={setShowChgEmail} />

      <ChangePasswordModal show={showChgPass} setShow={setShowChgPass} />

      <UserPageDeleteAccountModal show={showDel} setShow={setShowDel} />

      <PageTitle>Overview</PageTitle>
      <div
        className="column-gap-4"
        style={{
          display: "grid",
          gridTemplateColumns:
            "minmax( max-content, 1fr) 1fr minmax( max-content, 1fr )",
        }}
      >
        <div className="grid-col-1 text-secondary">Role</div>
        <div className="grid-col-1 fw-bold mb-3">
          {auth.account?.roles.join(', ')}
        </div>
        <div className="grid-col-1 text-secondary">Name</div>
        <div className="grid-col-1 fw-bold">{auth.account.name}</div>
        <div className="grid-col-3 mb-3">
          <Link onClick={(e) => { e.preventDefault(); setShowChgName(true); }} to={""}>
            Change
          </Link>
        </div>
        <div className="grid-col-1 text-secondary ">Email</div>
        <div className="grid-col-1 fw-bold ">{auth.account.emailAddress}</div>
        <div className="grid-col-3 mb-3">
          <Link onClick={(e) => { e.preventDefault(); setShowChgEmail(true); }} to={""}>
            Change
          </Link>
        </div>
        <div className="grid-col-1 text-secondary">Password</div>
        <div className="grid-col-1 fw-bold">***********</div>
        <div className="grid-col-3 mb-3">
          <Link onClick={(e) => { e.preventDefault(); setShowChgPass(true); }} to={""}>
            Change
          </Link>
        </div>
        <div className="grid-col-1 text-secondary">Account Created</div>
        <div className="grid-col-1 mb-3">
            {showInstantLocalDateTime(Instant.parse(auth.account.createdWhen as unknown as string))}
        </div>
      </div>
      <PageTitle>Notifications</PageTitle>
      {canSee(NotificationType.MonthlyApplication) && (
      <div className="grid-col-2 mb-2  d-flex align-items-center">   
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="monthly-apps-toggle"
            checked={enableNotificationForMonthlyApplication}
            onChange={() =>
              changeNotificationStatus(
                !enableNotificationForMonthlyApplication,
                NotificationType.MonthlyApplication
              )
            }
          />
        </div>
        <div>Monthly Applications
          
        <div className="text-muted" style={{fontSize: ".9rem"}}>Receive a real-time email when a monthly application is received.</div>
        </div>
      </div>
      )}
      {canSee(NotificationType.MonthlyApplicationDaily) && (
      <div className="grid-col-2 d-flex mb-2 align-items-center">
        
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="monthly-apps-daily-toggle"
            checked={enableNotificationForMonthlyApplicationDaily}
            onChange={() =>
              changeNotificationStatus(
                !enableNotificationForMonthlyApplicationDaily,
                NotificationType.MonthlyApplicationDaily
              )
            }
          />
        </div>
        <div>Monthly Applications (Daily)
          <div className="text-muted" style={{fontSize: ".9rem"}}>Receive a daily email with the number of monthly applications received that day.</div>
        </div>
      </div>
      )}
      {canSee(NotificationType.RenewalRetriesExhausted) && (
      <div className="grid-col-2 mb-2  d-flex align-items-center">
        
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="monthly-apps-daily-toggle"
            checked={enableNotificationForRenewalRetries}
            onChange={() =>
              changeNotificationStatus(
                !enableNotificationForRenewalRetries,
                NotificationType.RenewalRetriesExhausted
              )
            }
          />
        </div>
  
        <div>Monthly Renewals Retries Exhausted
          <div className="text-muted" style={{fontSize: ".9rem"}}>Receive an email when subscription renewal failures have exceeded the maximum number of retries.</div>
        </div>
      </div>
      )}
      {canSee(NotificationType.SubscriptionRenewalDeactivationSummary) && (
        <div className="grid-col-2 mb-2 d-flex align-items-center">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="monitor-renewal-deactivation-summary-toggle"
              checked={enableNotificationForRenewalDeactivationSummary}
              onChange={() =>
                changeNotificationStatus(
                  !enableNotificationForRenewalDeactivationSummary,
                  NotificationType.SubscriptionRenewalDeactivationSummary
                )
              }
            />
          </div>

          <div>
            Renewal Deactivation Summary
            <div className="text-muted" style={{ fontSize: ".9rem" }}>
              Receive a summary of subscriptions that failed renewal, were invoiced, and then cancelled.
            </div>
          </div>
        </div>
      )}
     
      {canSee(NotificationType.DisputeAlert) && (
      <div className="grid-col-2 mb-2 d-flex align-items-center">
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="dispute-alert-toggle"
            checked={enableNotificationForDispute}
            onChange={() =>
              changeNotificationStatus(
                !enableNotificationForDispute,
                NotificationType.DisputeAlert
              )
            }
          />
        </div>

        <div>
          Dispute Alert
          <div className="text-muted" style={{ fontSize: ".9rem" }}>
            Receive an email when a payment dispute is opened so you can review and upload evidence.
          </div>
        </div>
      </div>
      )}
      {canSee(NotificationType.ParkerSubscriptionCancellation) && (
      <div className="grid-col-2 mb-2 d-flex align-items-center">
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="parker-cancellation-toggle"
            checked={enableNotificationForParkerSubscriptionCancellation}
            onChange={() =>
              changeNotificationStatus(
                !enableNotificationForParkerSubscriptionCancellation,
                NotificationType.ParkerSubscriptionCancellation
              )
            }
          />
        </div>

        <div>
          Parker Subscription Cancellation
          <div className="text-muted" style={{ fontSize: ".9rem" }}>
            Receive an email when a parker cancels their plan subscription.
          </div>
        </div>
      </div>
      )}
      {canSee(NotificationType.ParkerSubscriptionModified) && (
      <div className="grid-col-2 mb-2 d-flex align-items-center">
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="parker-subscription-modified-toggle"
            checked={enableNotificationForParkerSubscriptionModified}
            onChange={() =>
              changeNotificationStatus(
                !enableNotificationForParkerSubscriptionModified,
                NotificationType.ParkerSubscriptionModified
              )
            }
          />
        </div>

        <div>
          Parker Subscription Modification
          <div className="text-muted" style={{ fontSize: ".9rem" }}>
            Receive a notification when an account changes spaces on their plan, or adds or cancels group parkers.
          </div>
        </div>
      </div>
      )}
      {canSee(NotificationType.ValidationRequestCreated) && (
      <div className="grid-col-2 mb-2 d-flex align-items-center">
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="monitor-validation-request-toggle"
            checked={enableNotificationForValidationRequestCreated}
            onChange={() =>
              changeNotificationStatus(
                !enableNotificationForValidationRequestCreated,
                NotificationType.ValidationRequestCreated
              )
            }
          />
        </div>

        <div>
          Validation Request Submitted
          <div className="text-muted" style={{ fontSize: ".9rem" }}>
            Receive a notification when a parker submits a new validation request.
          </div>
        </div>
        
      </div>
      )}
      {canSee(NotificationType.ValidationRequestPurchased) && (
      <div className="grid-col-2 mb-2 d-flex align-items-center">
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="monitor-validation-purchased-toggle"
            checked={enableNotificationForValidationRequestPurchased}
            onChange={() =>
              changeNotificationStatus(
                !enableNotificationForValidationRequestPurchased,
                NotificationType.ValidationRequestPurchased
              )
            }
          />
        </div>

        <div>
          Validation Request Purchased
          <div className="text-muted" style={{ fontSize: ".9rem" }}>
            Receive a notification when a parker completes a validation request purchase.
          </div>
        </div>
      </div>
      )}
      {canSee(NotificationType.TransientParkerPaid) && (
      <div className="grid-col-2 mb-2 d-flex align-items-center">
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="monitor-transient-parker-paid-toggle"
            checked={enableNotificationForTransientParkerPaid}
            onChange={() =>
              changeNotificationStatus(
                !enableNotificationForTransientParkerPaid,
                NotificationType.TransientParkerPaid
              )
            }
          />
        </div>

        <div>
          Transient Parker Paid
          <div className="text-muted" style={{ fontSize: ".9rem" }}>
            Receive a notification when a transient parker makes a payment.
          </div>
        </div>
      </div>
      )}
      {canSee(NotificationType.SubscriptionRenewalSummary) && (
        <div className="grid-col-2 mb-2 d-flex align-items-center">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="monitor-renewals-summary-toggle"
              checked={enableNotificationForRenewalsSummary}
              onChange={() =>
                changeNotificationStatus(
                  !enableNotificationForRenewalsSummary,
                  NotificationType.SubscriptionRenewalSummary
                )
              }
            />
          </div>

          <div>
            Subscription Renewal Summary
            <div className="text-muted" style={{ fontSize: ".9rem" }}>
              Receive a summary of subscription renewal attempts and results.
            </div>
          </div>
        </div>
      )}
      {canSee(NotificationType.ACHPaymentFailure) && (
        <div className="grid-col-2 mb-2 d-flex align-items-center">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="monitor-renewals-summary-toggle"
              checked={enableNotificationForACHPaymentFailure}
              onChange={() =>
                changeNotificationStatus(
                  !enableNotificationForACHPaymentFailure,
                  NotificationType.ACHPaymentFailure
                )
              }
            />
          </div>

          <div>
            ACH Payment Failure
            <div className="text-muted" style={{ fontSize: ".9rem" }}>
              Receive a notification when an ACH payment has failed and requires follow up.
            </div>
          </div>
        </div>
      )}
    </LargePage>
  );
}
